import { chatwoot, kanbanApi } from './chatwoot'
import type { ChatwootMessage, Conversation } from '@/types/chatwoot'

export interface ConversationsMeta {
  mine_count: number
  assigned_count: number
  unassigned_count: number
  all_count: number
}

interface ConversationsResponse {
  data: {
    meta: ConversationsMeta
    payload: Conversation[]
  }
}

export interface FetchPageResult {
  conversations: Conversation[]
  meta: ConversationsMeta
}

export interface FetchPageParams {
  page: number
  status?: 'open' | 'resolved' | 'pending' | 'snoozed'
  assigneeType?: 'me' | 'unassigned' | 'assigned' | 'all'
}

export async function fetchConversationsPage(
  params: FetchPageParams,
): Promise<FetchPageResult> {
  const { data } = await chatwoot.get<ConversationsResponse>('/conversations', {
    params: {
      page: params.page,
      status: params.status ?? 'open',
      assignee_type: params.assigneeType ?? 'all',
    },
  })
  return {
    conversations: data.data.payload,
    meta: data.data.meta,
  }
}

export async function fetchConversation(id: number): Promise<Conversation> {
  const { data } = await chatwoot.get<Conversation>(`/conversations/${id}`)
  return data
}

// Atalho de bootstrap: traz TODAS as conversas open em 1 request ao
// kanban-api (que faz SQL direto no Postgres do Chatwoot). Substitui
// as 61 paginas REST do load() anterior. Se falhar, o store cai
// automaticamente para `fetchConversationsPage` em loop.
export async function fetchAllConversationsBoot(): Promise<FetchPageResult> {
  const { data } = await kanbanApi.get<ConversationsResponse>('/conversations')
  return {
    conversations: data.data.payload,
    meta: data.data.meta,
  }
}

interface MessagesResponse {
  payload: ChatwootMessage[]
}

export async function fetchMessages(
  conversationId: number,
  perPage = 5,
): Promise<ChatwootMessage[]> {
  const { data } = await chatwoot.get<MessagesResponse>(
    `/conversations/${conversationId}/messages`,
    { params: { per_page: perPage } },
  )
  return data.payload
}

// Unica inbox com janela de 24h: WhatsApp Cloud API oficial (Meta).
const WHATSAPP_INBOX_ID = 8

// Envia mensagem (ou nota privada) numa conversation. `message_type: 'outgoing'`
// é uma resposta do agente; `private: true` torna a mensagem visível só pro
// time do Chatwoot (não vai pro cliente no inbox).
export async function sendMessage(
  conversationId: number,
  content: string,
  isPrivate = false,
  knownInboxId?: number,
): Promise<ChatwootMessage> {
  // Decisao temporal: nunca usar o `can_reply` carregado ao abrir o drawer.
  // A consulta acontece no caminho de envio, imediatamente antes do POST.
  //
  // So a inbox 8 (WhatsApp Cloud API oficial) tem janela de 24h. As demais
  // (Uazapi nas inboxes 1 e 2, Meta nas 3 e 5, TikTok na 7) nao tem janela, e
  // por isso nao pagam o GET de preflight. Quando o chamador sabe a inbox, essa
  // decisao acontece antes da rede.
  //
  // Fail-open deliberado: bloqueia so quando o servidor CONFIRMA a janela
  // fechada (`can_reply === false`). Consulta que nao responde, ou resposta sem
  // o campo, deixa o envio seguir e a Meta decide; a auditoria de erros de
  // envio apanha o que for recusado. O fail-closed anterior transformava
  // qualquer falha de infraestrutura em silencio no atendimento: em 09/09 o
  // injector do Chatwoot recebeu 401 no preflight e derrubou o envio humano da
  // inbox 8 de ~55 por dia para 4.
  const needsWindowCheck =
    !isPrivate && (knownInboxId === undefined || knownInboxId === WHATSAPP_INBOX_ID)
  if (needsWindowCheck) {
    let fresh: Conversation | null = null
    try {
      fresh = await fetchConversation(conversationId)
    } catch {
      fresh = null
    }
    if (!fresh) {
      console.warn(
        `[kanban] janela de 24h nao confirmada para a conversa ${conversationId}; envio seguiu.`,
      )
    } else if (fresh.inbox_id === WHATSAPP_INBOX_ID && fresh.can_reply === false) {
      throw new Error(
        'A janela de 24 horas do WhatsApp está fechada. O texto foi preservado; selecione um template aprovado no Chatwoot.',
      )
    }
  }
  const { data } = await chatwoot.post<ChatwootMessage>(
    `/conversations/${conversationId}/messages`,
    {
      content,
      message_type: 'outgoing',
      private: isPrivate,
    },
  )
  return data
}
